import React, { FC, useEffect, useMemo, useState } from 'react';

import {
  Button,
  Center,
  Checkbox,
  Flex,
  Heading,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import { getPackagesBySKU } from 'api/order';
import { fetchSites } from 'api/site';
import { UserPill } from 'components/core/UserPill';
import { DataTable } from 'components/modules/DataTable';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';
import { useSKU } from 'hooks';
import { useIsSuperAdmin, washHub } from 'store/slices';
import getSortedData from 'utils/getSortedData';

import {
  applyOpenOrdersFilter,
  blankRepeatedGroupFields,
  buildCasesPerDay,
  buildOrdersByCustomer,
  buildTotalCases,
  CasesPerDayRow,
  currentWeekRange,
  fetchOrdersForSummary,
  NameMap,
  OrdersByCustomerRow,
  TotalCasesRow,
} from './ordersSummary';
import {
  buildCasesPerDayColumns,
  buildOrdersByCustomerColumns,
  buildTotalCasesColumns,
} from './ordersSummaryColumns';
import {
  downloadOrdersSummaryExcel,
  printOrdersSummary,
} from './ordersSummaryExport';

const TransferHubWashhub = '6552e877b476bf1a0f578a06';

const ordersByCustomerColumns = buildOrdersByCustomerColumns();
const casesPerDayColumns = buildCasesPerDayColumns();
const totalCasesColumns = buildTotalCasesColumns();

type SummaryTableProps<T extends { key: string }> = {
  title: string;
  data: T[];
  columns: ColumnDef<T, any>[];
  isLoading: boolean;
  maxH?: string;
};

const SummaryTable = <T extends { key: string }>({
  title,
  data,
  columns,
  isLoading,
  maxH = '320px',
}: SummaryTableProps<T>) => {
  const tableWidth =
    columns.reduce(
      (sum, column) =>
        sum + (typeof column.size === 'number' ? column.size : 0),
      0,
    ) + 10;

  return (
    <Flex direction="column" mt={4} w={`${tableWidth}px`}>
      <Heading fontSize="18px" mb={2}>
        {title}
      </Heading>
      <DataTable<T>
        data={data}
        columns={columns}
        isLoading={isLoading}
        getRowId={(row) => row.key}
        maxH={maxH}
        overflowX="hidden"
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="16px">No orders in this range</Text>
          </Center>
        )}
      />
    </Flex>
  );
};

const toNameMap = (items?: Array<{ _id: string; name: string }>): NameMap =>
  (items || []).reduce<NameMap>((acc, item) => {
    acc[item._id] = item.name;
    return acc;
  }, {});

const OrdersSummaryView: FC = () => {
  const location = useLocation();
  const isSuperAdmin = useIsSuperAdmin();
  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const allWashHubs = washHub.selectors.useAllWashHubs();

  const [formHubId, setFormHubId] = useState(selectedWashHub?._id ?? '');
  const [startDate, setStartDate] = useState<Date>(
    () => currentWeekRange().startDate,
  );
  const [endDate, setEndDate] = useState<Date>(
    () => currentWeekRange().endDate,
  );
  const [openOnly, setOpenOnly] = useState(false);

  useEffect(() => {
    setFormHubId(selectedWashHub?._id ?? '');
  }, [selectedWashHub?._id]);

  const washHubOptions = useMemo(() => {
    const hubs = (allWashHubs || []).filter(
      (hub) => hub._id !== TransferHubWashhub,
    );

    return getSortedData(hubs);
  }, [allWashHubs]);

  const hasDateRange = Boolean(startDate && endDate);

  const { data: packageSkus } = useSKU(
    'package',
    formHubId || undefined,
    undefined,
    undefined,
    true,
  );
  const { data: sites } = useQuery(['orders-summary-sites', formHubId], () =>
    fetchSites(formHubId || undefined, 1),
  );
  const {
    data: packagesBySku = [],
    isLoading: packagesLoading,
    isFetching: packagesFetching,
  } = useQuery(
    ['orders-summary-packages-by-sku', formHubId],
    () => getPackagesBySKU(formHubId),
    {
      enabled: Boolean(formHubId),
    },
  );

  const skuNames = useMemo(() => toNameMap(packageSkus), [packageSkus]);
  const siteNames = useMemo(() => toNameMap(sites), [sites]);
  const readyBySkuId = useMemo(() => {
    const map: Record<string, number> = {};

    packagesBySku.forEach((item) => {
      const skuId = item?.packageSku?._id;

      if (skuId) {
        map[skuId] = Number(item.readyCount) || 0;
      }
    });

    return map;
  }, [packagesBySku]);

  const { data: orders = [], isLoading, isFetching, refetch } = useQuery(
    [
      'orders-summary',
      formHubId,
      format(startDate, 'yyyy-MM-dd'),
      format(endDate, 'yyyy-MM-dd'),
    ],
    () =>
      fetchOrdersForSummary({
        washHub: formHubId,
        startDate,
        endDate,
      }),
    {
      enabled: hasDateRange,
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: 'always' as any,
    },
  );

  useEffect(() => {
    refetch();
  }, [location.key]);

  const summaryOrders = useMemo(() => applyOpenOrdersFilter(orders, openOnly), [
    orders,
    openOnly,
  ]);
  const ordersByCustomer = useMemo(
    () => buildOrdersByCustomer(summaryOrders, skuNames, siteNames),
    [summaryOrders, skuNames, siteNames],
  );
  const casesPerDay = useMemo(
    () => buildCasesPerDay(summaryOrders, skuNames, siteNames),
    [summaryOrders, skuNames, siteNames],
  );
  const totalCases = useMemo(
    () => buildTotalCases(summaryOrders, skuNames, siteNames, readyBySkuId),
    [summaryOrders, skuNames, siteNames, readyBySkuId],
  );

  const displayOrdersByCustomer = useMemo(
    () =>
      blankRepeatedGroupFields(ordersByCustomer, ['deliveryDate', 'customer']),
    [ordersByCustomer],
  );
  const displayCasesPerDay = useMemo(
    () => blankRepeatedGroupFields(casesPerDay, ['deliveryDate']),
    [casesPerDay],
  );

  const tablesLoading =
    hasDateRange &&
    (isLoading ||
      isFetching ||
      (Boolean(formHubId) && (packagesLoading || packagesFetching)));

  const onSearchPress = (nextStart: Date | null, nextEnd: Date | null) => {
    const nextStartDate = nextStart || startDate;
    const nextEndDate = nextEnd || endDate;
    const sameRange =
      format(nextStartDate, 'yyyy-MM-dd') === format(startDate, 'yyyy-MM-dd') &&
      format(nextEndDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd');

    setStartDate(nextStartDate);
    setEndDate(nextEndDate);

    if (sameRange) {
      refetch();
    }
  };

  const onSearchClear = () => {
    const { startDate: weekStart, endDate: weekEnd } = currentWeekRange();
    setStartDate(weekStart);
    setEndDate(weekEnd);
  };

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex align="flex-start" justify="space-between">
        <Heading mb={6} fontSize="24px">
          Orders Summary
        </Heading>
        <UserPill />
      </Flex>

      <Flex align="flex-end" gap="20px" flexWrap="wrap" mb={1}>
        <Stack spacing={1} minW="240px" maxW="240px" w="100%" mb={6}>
          <Text color="textColor">Wash Hub</Text>
          <Select
            value={formHubId}
            onChange={(event) => setFormHubId(event.target.value)}>
            {(isSuperAdmin || formHubId === '') && (
              <option value="">Global View</option>
            )}
            {washHubOptions.map((hub) => (
              <option key={hub._id} value={hub._id}>
                {hub.name}
              </option>
            ))}
          </Select>
        </Stack>
        <DateRangeInputExt
          startDate={startDate}
          endDate={endDate}
          onSearchPress={onSearchPress}
          onSearchClear={onSearchClear}
          searchLabel="Refresh"
        />
        <Checkbox
          mb={6}
          colorScheme="primary"
          isChecked={openOnly}
          onChange={(event) => setOpenOnly(event.target.checked)}>
          Open orders only
        </Checkbox>
        <Button
          ml={3}
          mb={6}
          minW="200px"
          isDisabled={tablesLoading}
          onClick={() =>
            downloadOrdersSummaryExcel({
              fileName: `washhub-${
                selectedWashHub?.name || 'all'
              }-orders-summary.xls`,
              ordersByCustomer,
              casesPerDay,
              totalCases,
            })
          }>
          Export to CSV
        </Button>
        <Button
          ml={3}
          mb={6}
          minW="200px"
          isDisabled={tablesLoading}
          onClick={() =>
            printOrdersSummary({
              hubName:
                (formHubId === ''
                  ? 'Global View'
                  : washHubOptions.find((hub) => hub._id === formHubId)
                      ?.name) ||
                selectedWashHub?.name ||
                'All',
              startDate,
              endDate,
              ordersByCustomer,
              casesPerDay,
              totalCases,
            })
          }>
          Print
        </Button>
      </Flex>

      {!hasDateRange ? (
        <Center p={8}>
          <Text fontSize="16px">
            Select a date or date range to see the orders summary.
          </Text>
        </Center>
      ) : (
        <>
          <Flex gap={8}>
            <SummaryTable<OrdersByCustomerRow>
              title="Orders By Customer"
              data={displayOrdersByCustomer}
              columns={ordersByCustomerColumns}
              isLoading={tablesLoading}
              maxH="640px"
            />

            <SummaryTable<CasesPerDayRow>
              title="Cases Per Day"
              data={displayCasesPerDay}
              columns={casesPerDayColumns}
              isLoading={tablesLoading}
            />
            <SummaryTable<TotalCasesRow>
              title="Total Cases"
              data={totalCases}
              columns={totalCasesColumns}
              isLoading={tablesLoading}
            />
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default OrdersSummaryView;
